"use client";

import { ArrowRight } from "lucide-react";
import { useCallback } from "react";

interface PaginationButtonProps {
	hasPrevious?: boolean;
	hasMore?: boolean;
	isLoading: boolean;
	loadPrevious?: () => Promise<void>;
	loadMore?: () => Promise<void>;
	className?: string;
}

// Previous Button Component (matches carousel-buttons style)
export function PreviousButton({
	hasPrevious,
	isLoading,
	loadPrevious,
	className,
}: Pick<PaginationButtonProps, "hasPrevious" | "isLoading" | "loadPrevious" | "className">) {
	const handlePrevious = useCallback(
		async (event: React.MouseEvent) => {
			event.preventDefault();
			event.stopPropagation();

			if (hasPrevious && !isLoading && loadPrevious) {
				await loadPrevious();
			}
		},
		[hasPrevious, isLoading, loadPrevious],
	);

	return (
		<button
			type="button"
			onClick={handlePrevious}
			disabled={!hasPrevious || isLoading}
			className={
				className ||
				"relative z-10 text-black border-gray-300 h-10 w-20 rounded-md flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed bg-gray-100/50 hover:bg-gray-200 border touch-manipulation select-none cursor-pointer"
			}
			aria-label="Previous item"
			style={{ pointerEvents: "auto", WebkitTouchCallout: "none" }}
		>
			{isLoading ? (
				<div className="w-4 h-4 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" />
			) : (
				<ArrowRight className="w-4 h-4 rotate-180" />
			)}
		</button>
	);
}

// Next Button Component (matches carousel-buttons style)
export function NextButton({
	hasMore,
	isLoading,
	loadMore,
	className,
}: Pick<PaginationButtonProps, "hasMore" | "isLoading" | "loadMore" | "className">) {
	const handleNext = useCallback(
		async (event: React.MouseEvent) => {
			event.preventDefault();
			event.stopPropagation();

			if (hasMore && !isLoading && loadMore) {
				await loadMore();
			}
		},
		[hasMore, isLoading, loadMore],
	);

	return (
		<button
			type="button"
			onClick={handleNext}
			disabled={!hasMore || isLoading}
			className={
				className ||
				"relative z-10 text-black border-gray-300 h-10 w-20 rounded-md flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed bg-gray-100/50 hover:bg-gray-200 border touch-manipulation select-none cursor-pointer"
			}
			aria-label="Next item"
			style={{ pointerEvents: "auto", WebkitTouchCallout: "none" }}
		>
			{isLoading ? (
				<div className="w-4 h-4 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" />
			) : (
				<ArrowRight className="w-4 h-4" />
			)}
		</button>
	);
}
